import { environmentHttpClient } from "client/Api/httpClient";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";

export type ColoredValueModel<TValue> = {
	value: TValue;
	color: ResultMapperColor | null;
};

export type GeneralIntegrationFlowResultItemModel = {
	title: ColoredValueModel<string> | null;
	dateTime: ColoredValueModel<string> | null;
	subtitle1: ColoredValueModel<string> | null;
	subtitle2: ColoredValueModel<string> | null;
	subtitle3: ColoredValueModel<string> | null;
	description: ColoredValueModel<string> | null;
	link: string | null;
};

export type PlanningIntegrationFlowResultItemModel = {
	title: ColoredValueModel<string> | null;
	startDateTime: ColoredValueModel<string>;
	endDateTime: ColoredValueModel<string>;
	link: string | null;
	subtitle1: ColoredValueModel<string> | null;
	subtitle2: ColoredValueModel<string> | null;
	subtitle3: ColoredValueModel<string> | null;
	description: ColoredValueModel<string> | null;
	detail1: ColoredValueModel<string> | null;
	detail2: ColoredValueModel<string> | null;
	detail3: ColoredValueModel<string> | null;
	detail4: ColoredValueModel<string> | null;
	detail5: ColoredValueModel<string> | null;
};

export enum GeneralIntegrationFlowLiveInformationResultStatus {
	Success = 1,
	AuthenticationIsRequired = 2,
	Failure = 3
}

export type GeneralIntegrationFlowResult = {
	status: GeneralIntegrationFlowLiveInformationResultStatus.Failure;
} | {
	status: GeneralIntegrationFlowLiveInformationResultStatus.AuthenticationIsRequired;
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
} | {
	status: GeneralIntegrationFlowLiveInformationResultStatus.Success;
	items: readonly GeneralIntegrationFlowResultItemModel[];
};

export type LiveInformationRequestModel = {
	searchTerm: OptionalModel<string>;
};

export function getGeneralLiveInformation(
	integrationId: number,
	model: LiveInformationRequestModel,
	signal?: AbortSignal
): Promise<GeneralIntegrationFlowResult> {
	return environmentHttpClient.get<GeneralIntegrationFlowResult>(
		`Workspace/LiveInformation/AppIntegration/General/${integrationId}`,
		model,
		{ signal }
	);
}

export type PlanningLiveInformationRequestModel = {
	startDateTime: string;
	endDateTime: string;
};

export enum PlanningIntegrationFlowLiveInformationResultStatus {
	Success = 1,
	AuthenticationRequired = 2,
	Failure = 3
}

export type PlanningIntegrationFlowResult = {
	status: PlanningIntegrationFlowLiveInformationResultStatus.Failure;
} | {
	status: PlanningIntegrationFlowLiveInformationResultStatus.AuthenticationRequired;
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
} | {
	status: PlanningIntegrationFlowLiveInformationResultStatus.Success;
	items: readonly PlanningIntegrationFlowResultItemModel[];
};

export function getPlanningLiveInformation(
	integrationId: number,
	model: PlanningLiveInformationRequestModel,
	signal?: AbortSignal
): Promise<PlanningIntegrationFlowResult> {
	return environmentHttpClient.get<PlanningIntegrationFlowResult>(
		`Workspace/LiveInformation/AppIntegration/Planning/${integrationId}`,
		model,
		{ signal }
	);
}

export type GeneralIntegrationFlowConfiguration = {
	doesSupportSearch: boolean;
};

export function getFlowConfiguration(
	integrationId: number,
	signal?: AbortSignal
): Promise<GeneralIntegrationFlowConfiguration> {
	return environmentHttpClient.get<GeneralIntegrationFlowConfiguration>(
		`Workspace/LiveInformation/AppIntegration/${integrationId}/FlowConfiguration`,
		undefined,
		{ signal }
	);
}
